import { useCallback, useLayoutEffect, useMemo } from "react";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import { useOnMount } from "~/common/hooks/useOnMount";
import {
  addAllToCartThunk,
  createWishlistThunk,
  selectWishlistThunk,
  setEntryUIState,
  setSelectedEntryIds,
  updateWishlistCommentThunk,
  wishlistPageMounted,
} from "~/features/wishlist/redux/wishlistSlice";
import { useDispatch, useSelector } from "react-redux";
import { type RootState } from "~/app/store";
import Spinner from "~/common/components/Spinner/Spinner";
import {
  getSelectPriceParamsFromWishlistEntry,
  selectFullWishlist,
  selectIsSelectedWishlistProcessed,
  selectIsWishlistsLoading,
  selectSelectedWishlist,
  selectSelectedWishlistId,
  selectShowATCNotReadyMsg,
  selectShowWSErrorMsg,
  selectWishlists,
} from "~/features/wishlist/redux/wishlistSelectors";

import classes from "~/pages/WishlistPage/WishlistPage.module.less";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { canSeeSalesFinder } from "~/features/navigation/components/headerMenu/DesktopPersonalNav";
import { userIsCustomer, userIsRegistered } from "~/domains/user/utils/roles";
import { AddToCartButton, RequestOfferButton } from "~/pages/WishlistPage/components/buttons/WishlistButtons";
import { NoWishlistContent } from "~/pages/WishlistPage/components/NoWishlistContent";
import WishlistDropdown from "~/features/wishlist/components/WishlistDropdown";
import EditWishlistNamePopup from "~/features/wishlist/components/modals/EditWishlistNamePopup";
import { WishlistDetails } from "~/pages/WishlistPage/components/WishlistDetails/WishlistDetails";
import RenameWishlistButton from "~/pages/WishlistPage/components/buttons/RenameWishlistButton";
import { getCoreRowModel, getExpandedRowModel, useReactTable } from "@tanstack/react-table";
import { type ExpandedResolvedWishlistEntryModel } from "~/features/wishlist/types/model";
import { DeleteWishlistButton } from "~/pages/WishlistPage/components/buttons/DeleteWishlistButton";
import { ShareWishlistButton } from "~/pages/WishlistPage/components/buttons/ShareWishlistButton";
import { CommentInput, useShowAndScrollInput } from "~/common/components/CommentInput/CommentInput";
import { CommentButton } from "~/pages/WishlistPage/components/buttons/CommentButton";
import { useTranslation } from "react-i18next";
import { getTariff, selectNetPrice } from "~/domains/product/utils/productDescriptorUtils";
import { IncludesSurchargeDisclaimer } from "~/domains/pricing/components/IncludesSurchargeDisclaimer";
import { canSeeAddToCartButton } from "~/utils/features";
import { Alert } from "~/common/mui/Alert/Alert";

export const WishlistPage = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  useOnMount(() => {
    dispatch(wishlistPageMounted());
  });

  const isLoading = useSelector(selectIsWishlistsLoading);
  const wishlists = useSelector(selectWishlists);

  const hasWishlists = wishlists.length > 0;
  const canPurchase = canSeeAddToCartButton();
  const isRegistered = userIsRegistered();
  const isCustomer = userIsCustomer();

  const [createWishlistModal, openCreateWishlistModal] = useCreateWishlistPopup();

  const selectedWishlistId = useSelector(selectSelectedWishlistId);
  const selectedWishlist = useSelector(selectSelectedWishlist);

  const isProcessed = useSelector(selectIsSelectedWishlistProcessed);
  const selectedWishlistIsLoading = selectedWishlist && !isProcessed;

  const showWSErrorMsg = useSelector(selectShowWSErrorMsg);
  const showATCNotReadyMsg = useSelector(selectShowATCNotReadyMsg);

  const resolvedWishlist = useSelector((state: RootState) =>
    selectedWishlistId ? selectFullWishlist(state, selectedWishlistId) : null,
  );
  const entries = resolvedWishlist?.entries ?? [];

  const selectedEntryIds = useSelector((state: RootState) => state.wishlistUI.selectedEntryIds);

  const makeSetSaleEnabled = (index: number) => (isSaleEnabled: boolean) => {
    const entry = entries[index];
    if (entry) {
      dispatch(setEntryUIState({ entryId: entry.id, isSaleEnabled }));
    }
  };

  const { onSelectWishlist, onSaveComment } = useMemo(() => {
    return {
      onSelectWishlist: (wishlistId: string | undefined) => dispatch(selectWishlistThunk(wishlistId!)),
      onSaveComment: (comment: string) => {
        if (selectedWishlistId) {
          dispatch(updateWishlistCommentThunk(selectedWishlistId, comment));
        }
      },
    };
  }, [dispatch, selectedWishlistId]);

  const onAddSelected = () => {
    dispatch(addAllToCartThunk());
  };

  const hasAnySurcharge = useSelector((state: RootState) => {
    for (const entry of entries) {
      const priceParams = getSelectPriceParamsFromWishlistEntry(entry, entry.packCode);
      const netPrice = selectNetPrice(priceParams)(state);
      if ((netPrice?.surcharge ?? 0) > 0) {
        return true;
      }
    }
    return false;
  });

  const hasAnyTariffSurcharge = useMemo(() => {
    for (const entry of entries) {
      if (getTariff(entry.product)) {
        return true;
      }
    }
    return false;
  }, [entries]);

  const rowSelection = useMemo(() => {
    return selectedEntryIds.reduce((acc, id) => ({ ...acc, [id]: true }), {});
  }, [selectedEntryIds]);

  const onRowSelectionChange = useCallback(
    (updaterOrValue: any) => {
      const newSelection = typeof updaterOrValue === "function" ? updaterOrValue(rowSelection) : updaterOrValue;
      dispatch(setSelectedEntryIds(Object.keys(newSelection).filter((id) => !!newSelection[id])));
    },
    [dispatch, rowSelection],
  );

  const tableRef = useReactTable<ExpandedResolvedWishlistEntryModel>({
    data: entries,
    columns: [{ id: "" }],
    state: {
      rowSelection,
    },
    onRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (data) => data.id,
    getExpandedRowModel: getExpandedRowModel(),
    enableRowSelection: (row) => row.original.isSelectable,
    getRowCanExpand: () => true,
  });

  let ctaButton = null;
  if (!!selectedWishlistId && canPurchase) {
    ctaButton = (
      <AddToCartButton className={classes.addToCart} onClick={onAddSelected} disabled={selectedEntryIds.length === 0} />
    );
  } else if (isRegistered && canSeeSalesFinder()) {
    ctaButton = (
      <RequestOfferButton
        className={classes.addToCart}
        wishlistId={selectedWishlistId ?? ""}
        selectedEntryIds={selectedEntryIds}
      />
    );
  }

  const { val: commentVisible, toggle: toggleCommentVisibility, setFalse: hideComment } = useBooleanState(false);
  const [commentRef, toggleComment] = useShowAndScrollInput(toggleCommentVisibility);

  useLayoutEffect(() => {
    hideComment();
  }, [selectedWishlistId]);

  const wishlistSelect = (
    <WishlistDropdown
      wishlists={wishlists}
      selectedWishlist={selectedWishlistId ?? undefined}
      onNewWishlist={openCreateWishlistModal}
      onWishlistSelect={onSelectWishlist}
    />
  );

  return (
    <>
      {createWishlistModal}
      <PageTitle>{t("wishlistPage.title")}</PageTitle>

      {isLoading && <Spinner />}
      {!isLoading && !hasWishlists && <NoWishlistContent onCreateWishlist={openCreateWishlistModal} />}
      {!isLoading && hasWishlists && (
        <div className={classes.content}>
          <div className={classes.wishlistSelect}>{wishlistSelect}</div>

          {selectedWishlist ? (
            <>
              <div className={classes.actions}>
                <CommentButton
                  onClick={toggleComment}
                  isOpen={commentVisible}
                  hasComment={!!selectedWishlist.comment}
                />
                <ShareWishlistButton wishlist={selectedWishlist} />
                <RenameWishlistButton wishlist={selectedWishlist} />
                <DeleteWishlistButton wishlist={selectedWishlist} />
                {ctaButton}
              </div>

              {commentVisible && (
                <div className={classes.commentBlock}>
                  <div className={classes.commentBlockTitle}>{t("wishlistPage.comment.title")}</div>
                  <CommentInput
                    value={selectedWishlist.comment}
                    onChange={onSaveComment}
                    ref={commentRef}
                    label={t("wishlistPage.comment.label")}
                  />
                </div>
              )}

              <div className={classes.messages}>
                {showATCNotReadyMsg && <Alert severity={"info"}>{t("wishlistPage.fetchingInProgress")}</Alert>}
                {showWSErrorMsg && <Alert severity={"info"}>{t("wishlistPage.wsError")}</Alert>}
              </div>

              {selectedWishlistIsLoading && <Spinner />}
              {!selectedWishlistIsLoading && (
                <WishlistDetails
                  isCustomer={isCustomer}
                  tableRef={tableRef}
                  className={classes.details}
                  makeSetSaleEnabled={makeSetSaleEnabled}
                />
              )}
            </>
          ) : (
            <Spinner />
          )}

          <IncludesSurchargeDisclaimer
            hasSurcharge={hasAnySurcharge}
            tariffSurcharge={hasAnyTariffSurcharge}
            className={classes.surcharge}
          />
        </div>
      )}
    </>
  );
};

const useCreateWishlistPopup = () => {
  const { val: isModalOpen, setTrue, setFalse } = useBooleanState(false);

  const dispatch = useDispatch();
  const createWishlistCb = useCallback(
    async (name: string) => {
      setFalse();
      await dispatch(createWishlistThunk(name));
      // Re-fetching or selecting newly created wishlist will be handled by RTK mutation result if needed
      // or we can just rely on the user selecting it.
      // But legacy code tried to select it.
    },
    [dispatch, setFalse],
  );

  const modal = isModalOpen && <EditWishlistNamePopup onCancel={setFalse} onOk={createWishlistCb} />;
  return [modal, setTrue] as const;
};

export function createWishlistPage() {
  return <WishlistPage />;
}
